/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, JSX } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  Check,
  Close as ErrorValidation,
  Minus as PendingValidation,
} from '@adobe-commerce/elsie/icons';

import '@adobe-commerce/elsie/components/InputPassword/PasswordStatusIndicator/PasswordStatusIndicator.css';

type statusTypes = '' | 'success' | 'error' | 'pending';

export interface ValidateLengthConfigProps {
  status?: statusTypes;
  icon?: statusTypes;
  message?: string;
}

export interface PasswordStatusIndicatorProps {
  validateLengthConfig?: ValidateLengthConfigProps;
  uniqueSymbolsStatus?: statusTypes;
  minLength?: number;
  requiredCharacterClasses?: number;
}

const getIcon: { [key: string]: JSX.Element } = {
  pending: <PendingValidation />,
  success: <Check />,
  error: <ErrorValidation style={{ fill: 'red' }} />,
};

export const PasswordStatusIndicator: FunctionComponent<
  PasswordStatusIndicatorProps & HTMLAttributes<HTMLDivElement>
> = ({
  minLength = 0,
  requiredCharacterClasses = 0,
  uniqueSymbolsStatus = 'pending',
  validateLengthConfig = { status: '', icon: '', message: '' },
}) => {
  const translations = useText({
    chartTwoSymbols: 'Dropin.PasswordStatusIndicator.chartTwoSymbols',
    chartThreeSymbols: 'Dropin.PasswordStatusIndicator.chartThreeSymbols',
    chartFourSymbols: 'Dropin.PasswordStatusIndicator.chartFourSymbols',
  });

  const getMessage = (chart: number) => {
    switch (chart) {
      case 2:
        return translations.chartTwoSymbols;
      case 3:
        return translations.chartThreeSymbols;
      case 4:
        return translations.chartFourSymbols;
      default:
        return '';
    }
  };

  return (
    <div className={classes(['dropin-password-status-indicator'])}>
      {minLength > 0 ? (
        <div
          className={`dropin-password-status-indicator__item dropin-password-status-indicator__item--${validateLengthConfig.status}`}
          data-testid={`dropin-password-status-indicator__item--${validateLengthConfig.icon}`}
        >
          {getIcon[validateLengthConfig.icon as string]}
          <span className={`${validateLengthConfig.status}`}>
            {validateLengthConfig.message}
          </span>
        </div>
      ) : null}
      {requiredCharacterClasses >= 2 ? (
        <div
          className={`dropin-password-status-indicator__item dropin-password-status-indicator__item--${uniqueSymbolsStatus}`}
          data-testid={`dropin-password-status-indicator__item--${uniqueSymbolsStatus}`}
        >
          {getIcon[uniqueSymbolsStatus]}
          <span className={`pending`}>
            {getMessage(requiredCharacterClasses)}
          </span>
        </div>
      ) : null}
    </div>
  );
};
